// @ts-nocheck
/**
 * Metrics Column Configuration for Order By SKU
 *
 * Organizes the ~100+ columns from [app].[OrderDetail] into logical groups
 * with a curated default-visible subset. Remaining columns are available
 * via AG Grid's column chooser (side panel).
 *
 * Column groups appear as collapsible header bands in the grid.
 */

/* ─── Group definitions ─── */
export interface MetricsGroup {
	groupName: string;
	/** CSS class applied to the group header */
	headerClass?: string;
	/** Columns in display order */
	columns: string[];
	/** If true, columns start hidden (user can unhide via column chooser) */
	defaultHidden?: boolean;
}

export const METRICS_COLUMN_GROUPS: MetricsGroup[] = [
	/* ── Inventory ── visible by default */
	{
		groupName: 'Inventory',
		headerClass: 'obs-col-group--inventory',
		columns: ['OnHand', 'OnOrder', 'InTransit', 'ProjectedOnHand', 'StorageUnitQty', 'OrderStorageUnitQty'],
	},
	/* ── Order Info ── visible by default */
	{
		groupName: 'Order',
		headerClass: 'obs-col-group--order',
		columns: ['SuggestedQty', 'OrderQty', 'OrderQtyEaches', 'UOMConversion', 'SKUCost', 'SKURetail'],
	},
	/* ── Trailing 7-Day ── visible by default */
	{
		groupName: 'Trailing 7-Day',
		headerClass: 'obs-col-group--t7',
		columns: [
			'Trailing7DaySalesQty',
			'Trailing7DaySalesRetail',
			'Trailing7DayAvgSellPrice',
			'Trailing7DayGrossMarginPer',
			'Trailing7DaySellQtyPer',
			'Trailing7DayCompQtyPer',
			'Trailing7DayRetailDegradation',
			'Trailing7DayShippedQty',
			'Trailing7DayShippedAmt',
		],
	},
	/* ── Trailing 28-Day ── visible by default */
	{
		groupName: 'Trailing 28-Day',
		headerClass: 'obs-col-group--t28',
		columns: [
			'Trailing28DaySalesQty',
			'Trailing28DaySalesRetail',
			'Trailing28DayAvgSellPrice',
			'Trailing28DayGrossMarginPer',
			'Trailing28DaySellQtyPer',
			'Trailing28DayCompQtyPer',
			'Trailing28DayRetailDegradation',
			'Trailing28DayShippedQty',
			'Trailing28DayShippedAmt',
		],
	},
	/* ── AI Forecasts ── visible by default */
	{
		groupName: 'AI Forecasts',
		headerClass: 'obs-col-group--forecast',
		columns: ['GapDemand', 'Leading7DayAIForecastQty', 'Leading14DayAIForecastQty', 'Leading21DayAIForecastQty'],
	},
	/* ── Fiscal YTD ── visible by default */
	{
		groupName: 'Fiscal YTD',
		headerClass: 'obs-col-group--ytd',
		columns: [
			'FiscalYTDSalesQty',
			'FiscalYTDSalesRetail',
			'FiscalYTDAvgSellPrice',
			'FiscalYTDGrossMarginPer',
			'FiscalYTDSellQtyPer',
			'FiscalYTDCompQtyPer',
			'FiscalYTDRetailDegradation',
			'FiscalYTDShippedQty',
			'FiscalYTDShippedAmt',
		],
	},
	/* ── Fiscal LYTD (Last Year To Date) ── visible by default */
	{
		groupName: 'Fiscal LYTD',
		headerClass: 'obs-col-group--lytd',
		columns: [
			'FiscalLYTDSalesQty',
			'FiscalLYTDSalesRetail',
			'FiscalLYTDAvgSellPrice',
			'FiscalLYTDGrossMarginPer',
			'FiscalLYTDSellQtyPer',
			'FiscalLYTDCompQtyPer',
			'FiscalLYTDRetailDegradation',
			'FiscalLYTDShippedQty',
			'FiscalLYTDShippedAmt',
		],
	},

	/* ─── Hidden by default (available via column chooser) ─── */

	/* ── Weeks of Supply ── */
	{
		groupName: 'Weeks of Supply',
		headerClass: 'obs-col-group--wos',
		defaultHidden: true,
		columns: ['TargetWOS', 'CurrentWOS', 'ProjectedWOS'],
	},
	/* ── Trailing 14-Day ── */
	{
		groupName: 'Trailing 14-Day',
		headerClass: 'obs-col-group--t14',
		defaultHidden: true,
		columns: [
			'Trailing14DaySalesQty',
			'Trailing14DaySalesRetail',
			'Trailing14DayAvgSellPrice',
			'Trailing14DayGrossMarginPer',
			'Trailing14DaySellQtyPer',
			'Trailing14DayCompQtyPer',
			'Trailing14DaysRetailDegradation',
			'Trailing14DayShippedQty',
			'Trailing14DayShippedAmt',
		],
	},
	/* ── Trailing 21-Day ── */
	{
		groupName: 'Trailing 21-Day',
		headerClass: 'obs-col-group--t21',
		defaultHidden: true,
		columns: [
			'Trailing21DaySalesQty',
			'Trailing21DaySalesRetail',
			'Trailing21DayAvgSellPrice',
			'Trailing21DayGrossMarginPer',
			'Trailing21DaySellQtyPer',
			'Trailing21DayCompQtyPer',
			'Trailing21DayRetailDegradation',
			'Trailing21DayShippedQty',
			'Trailing21DayShippedAmt',
		],
	},
	/* ── Naive Forecasts ── */
	{
		groupName: 'Naive Forecasts',
		headerClass: 'obs-col-group--naive',
		defaultHidden: true,
		columns: [
			'Leading14DayNaiveForecastQty',
			'30DayNaiveForecastQty',
			'60DayNaiveForecastQty',
			'90DayNaiveForecastQty',
			'120DayNaiveForecastQty',
		],
	},
	/* ── Fiscal Last Week ── */
	{
		groupName: 'Fiscal Last Week',
		headerClass: 'obs-col-group--flw',
		defaultHidden: true,
		columns: [
			'FiscalLastWeekSalesQty',
			'FiscalLastWeekSalesRetail',
			'FiscalLastWeekAvgSellPrice',
			'FiscalLastWeekGrossMarginPer',
			'FiscalLastWeekSellQtyPer',
			'FiscalLastWeekCompQtyPer',
			'FiscalLastWeekRetailDegradation',
			'FiscalLastWeekShippedQty',
			'FiscalLastWeekShippedAmt',
		],
	},
	/* ── Fiscal WTD ── */
	{
		groupName: 'Fiscal WTD',
		headerClass: 'obs-col-group--wtd',
		defaultHidden: true,
		columns: [
			'FiscalWTDSalesQty',
			'FiscalWTDSalesRetail',
			'FiscalWTDAvgSellPrice',
			'FiscalWTDGrossMarginPer',
			'FiscalWTDSellQtyPer',
			'FiscalWTDCompQtyPer',
			'FiscalWTDRetailDegradation',
			'FiscalWTDShippedQty',
			'FiscalWTDShippedAmt',
		],
	},
	/* ── Fiscal Last Year ── */
	{
		groupName: 'Fiscal LY',
		headerClass: 'obs-col-group--ly',
		defaultHidden: true,
		columns: [
			'FiscalLYSalesQty',
			'FiscalLYSalesRetail',
			'FiscalLYAvgSellPrice',
			'FiscalLYGrossMarginPer',
			'FiscalLYSellQtyPer',
			'FiscalLYCompQtyPer',
			'FiscalLYRetailDegradation',
			'FiscalLYShippedQty',
			'FiscalLYShippedAmt',
		],
	},
	/* ── Shipping / Dates ── */
	{
		groupName: 'Shipping',
		headerClass: 'obs-col-group--ship',
		defaultHidden: true,
		columns: ['ShipMethodID', 'LastShipDate', 'LastSalesDate', 'DaysSinceLastShipment', 'DaysSinceLastSales'],
	},
];

/**
 * Build a lookup: columnField → { groupName, headerClass, defaultHidden }
 */
const buildColumnGroupMap = () => {
	const map: Record<string, { groupName: string; headerClass: string; defaultHidden: boolean }> = {};
	for (const g of METRICS_COLUMN_GROUPS) {
		for (const col of g.columns) {
			map[col] = {
				groupName: g.groupName,
				headerClass: g.headerClass || '',
				defaultHidden: g.defaultHidden || false,
			};
		}
	}
	return map;
};

const COLUMN_GROUP_MAP = buildColumnGroupMap();

/**
 * Columns that should never be grouped (identity / key columns).
 * These stay as top-level columns to the left of groups.
 */
const UNGROUPED_COLUMNS = new Set([
	'OrderDetailID',
	'BulkOrderDetailTmpID',
	'OrderID',
	'OrderKitID',
	'SKUID',
	'UOMID',
	'PlanogramAttributeID',
	'AdDetailID',
	'HealthCheckID',
	'InitialDataSource',
	'InitialUpdate',
	'DataSource',
	'LastUpdate',
	'LastUpdateUser',
	'IsReadOnly',
]);

/**
 * Post-processes flat AG Grid column definitions into grouped columns.
 *
 * - Columns that match a group are wrapped in { headerName, children: [...] }
 * - Columns in `defaultHidden` groups get `hide: true` unless the user
 *   has already toggled them via the column chooser (checked via `IsColumnEnabled`)
 * - Ungrouped / unrecognized columns stay as top-level columns
 *
 * @param columnDefs - Flat array of AG Grid ColDef objects
 * @returns Array of ColDef | ColGroupDef
 */
export const applyMetricsColumnGroups = (columnDefs: any[]): any[] => {
	if (!columnDefs?.length) return columnDefs;

	const ungrouped: any[] = [];
	const groupBuckets: Record<string, { headerName: string; headerClass: string; children: any[] }> = {};
	/** Maintain insertion order of groups */
	const groupOrder: string[] = METRICS_COLUMN_GROUPS.map((g) => g.groupName);

	for (const col of columnDefs) {
		const field = col.field;
		const groupInfo = field ? COLUMN_GROUP_MAP[field] : null;

		// Columns that already have children (existing groups), ungrouped columns,
		// or columns not in our metrics config stay as top-level
		if (col.children || !groupInfo || UNGROUPED_COLUMNS.has(field)) {
			ungrouped.push(col);
			continue;
		}

		const { groupName, headerClass, defaultHidden } = groupInfo;

		if (!groupBuckets[groupName]) {
			groupBuckets[groupName] = {
				headerName: groupName,
				headerClass: `obs-col-group ${headerClass}`,
				children: [],
			};
		}

		// Apply default visibility: if the group is defaultHidden,
		// force the column hidden. The user can unhide via the AG Grid
		// column chooser during their session. To persist, they save
		// a bookmark/custom view — which is the standard app pattern.
		const colDef = { ...col };
		if (defaultHidden) {
			colDef.hide = true;
		}

		groupBuckets[groupName].children.push(colDef);
	}

	// Assemble: ungrouped first, then groups in defined order
	const result: any[] = [...ungrouped];
	for (const gName of groupOrder) {
		if (groupBuckets[gName] && groupBuckets[gName].children.length > 0) {
			result.push(groupBuckets[gName]);
		}
	}

	return result;
};
